import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';

export enum MainFontName {
  Lato = 'Lato',
  WorkSans = 'WorkSans',
}

export enum CustomFontName {
  FuzzyBubbles = 'Fuzzy Bubbles',
  BarlowSemiCondensed = 'Barlow Semi Condensed',
  BodoniModa = 'Bodoni Moda',
  RampartOne = 'Rampart One',
  GochiHand = 'Gochi Hand',
  PlayfairDisplay = 'Playfair Display',
  AmaticSC = 'Amatic SC',
  Inconsolata = 'Inconsolata',
  Caveat = 'Caveat',
  RussoOne = 'Russo One',
}

export type FontFile = Record<string, string>;

export const FontName = { ...MainFontName, ...CustomFontName };

export enum FontFamily {
  Lato = 'Lato',
  WorkSans = 'WorkSans',
  AmaticSC = 'AmaticSC',
  BarlowSemiCondensed = 'BarlowSemiCondensed',
  BodoniModa = 'BodoniModa',
  Caveat = 'Caveat',
  GochiHand = 'GochiHand',
  Inconsolata = 'Inconsolata',
  PlayfairDisplay = 'PlayfairDisplay',
  RampartOne = 'RampartOne',
  RussoOne = 'RussoOne',
  FuzzyBubbles = 'FuzzyBubbles',
}

export enum FontStyle {
  Regular = 'regular',
  Italic = 'italic',
  Bold = 'bold',
  BoldItalic = 'bold-italic',
  Thin = 'thin',
  ThinItalic = 'thin-italic',
  Light = 'light',
  LightItalic = 'light-italic',
  Medium = 'medium',
  MediumItalic = 'medium-italic',
  Black = 'black',
  BlackItalic = 'black-italic',
}

export enum FontStyleFile {
  Regular = 'Regular',
  Italic = 'Italic',
  Bold = 'Bold',
  BoldItalic = 'BoldItalic',
  Thin = 'Thin',
  ThinItalic = 'ThinItalic',
  Light = 'Light',
  LightItalic = 'LightItalic',
  Medium = 'Medium',
  MediumItalic = 'MediumItalic',
  Black = 'Black',
  BlackItalic = 'BlackItalic',
}

export type FontInfo = Partial<
  Record<
    FontStyle,
    {
      key: string;
      file: string;
    }
  >
>;

// IMPORTANT: If a font is been added to this app, it needs to be added to the index.html file as well
export const MAIN_FONTS: Record<MainFontName, FontInfo> = {
  [MainFontName.Lato]: {
    [FontStyle.Regular]: {
      key: `Lato_400Regular`,
      file: '/fonts/Lato_400Regular.ttf',
    },
    [FontStyle.Bold]: {
      key: `Lato_700Bold`,
      file: '/fonts/Lato_700Bold.ttf',
    },
    [FontStyle.Italic]: {
      key: `Lato_400Regular_Italic`,
      file: '/fonts/Lato_400Regular_Italic.ttf',
    },
    [FontStyle.BoldItalic]: {
      key: `Lato_700Bold_Italic`,
      file: '/fonts/Lato_700Bold_Italic.ttf',
    },
    [FontStyle.Thin]: {
      key: `Lato_100Thin`,
      file: '/fonts/Lato_100Thin.ttf',
    },
    [FontStyle.ThinItalic]: {
      key: `Lato_100Thin_Italic`,
      file: '/fonts/Lato_100Thin_Italic.ttf',
    },
    [FontStyle.Light]: {
      key: `Lato_300Light`,
      file: '/fonts/Lato_300Light.ttf',
    },
    [FontStyle.LightItalic]: {
      key: `Lato_300Light_Italic`,
      file: '/fonts/Lato_300Light_Italic.ttf',
    },
    [FontStyle.Black]: {
      key: `Lato_900Black`,
      file: '/fonts/Lato_900Black.ttf',
    },
    [FontStyle.BlackItalic]: {
      key: `Lato_900Black_Italic`,
      file: '/fonts/Lato_900Black_Italic.ttf',
    },
  },
  [MainFontName.WorkSans]: {
    [FontStyle.Regular]: {
      key: `WorkSans_Regular`,
      file: '/fonts/WorkSans-Regular.ttf',
    },
    [FontStyle.Italic]: {
      key: `WorkSans_RegularItalic`,
      file: '/fonts/WorkSans-RegularItalic.ttf',
    },
    [FontStyle.Bold]: {
      key: `WorkSans_Bold`,
      file: '/fonts/WorkSans-Bold.ttf',
    },
    [FontStyle.Light]: {
      key: `WorkSans_Light`,
      file: '/fonts/WorkSans-Light.ttf',
    },
    [FontStyle.LightItalic]: {
      key: `WorkSans_LightItalic`,
      file: '/fonts/WorkSans-LightItalic.ttf',
    },
    [FontStyle.BoldItalic]: {
      key: `WorkSans_BoldItalic`,
      file: '/fonts/WorkSans-BoldItalic.ttf',
    },
    [FontStyle.Medium]: {
      key: `WorkSans_Medium`,
      file: '/fonts/WorkSans-Medium.ttf',
    },
    [FontStyle.MediumItalic]: {
      key: `WorkSans_MediumItalic`,
      file: '/fonts/WorkSans-MediumItalic.ttf',
    },
    [FontStyle.Thin]: {
      key: `WorkSans_Thin`,
      file: '/fonts/WorkSans-Thin.ttf',
    },
    [FontStyle.ThinItalic]: {
      key: `WorkSans_ThinItalic`,
      file: '/fonts/WorkSans-ThinItalic.ttf',
    },
    [FontStyle.Black]: {
      key: `WorkSans_Black`,
      file: '/fonts/WorkSans-Black.ttf',
    },
    [FontStyle.BlackItalic]: {
      key: `WorkSans_BlackItalic`,
      file: '/fonts/WorkSans-BlackItalic.ttf',
    },
  },
};

export const DEFAULT_LATO_FONT_STYLE_KEY = MAIN_FONTS[MainFontName.Lato][FontStyle.Regular]!.key;
export const DEFAULT_WORK_SANS_FONT_STYLE_KEY = MAIN_FONTS[MainFontName.WorkSans][FontStyle.Regular]!.key;

export const MAIN_FONTS_FILES: FontFile = Object.fromEntries(
  Object.values(MAIN_FONTS).flatMap(font => Object.values(font).map(style => [style.key, style.file])),
);

export const CUSTOM_FONTS: Record<CustomFontName, FontInfo> = {
  [CustomFontName.FuzzyBubbles]: {
    [FontStyle.Regular]: {
      key: `${FontFamily.FuzzyBubbles}${FontStyleFile.Regular}`,
      file: '/fonts/Fuzzy-Bubbles-Regular.ttf',
    },
    [FontStyle.Bold]: {
      key: `${FontFamily.FuzzyBubbles}${FontStyleFile.Bold}`,
      file: '/fonts/Fuzzy-Bubbles-Bold.ttf',
    },
  },
  [CustomFontName.BarlowSemiCondensed]: {
    [FontStyle.Regular]: {
      key: `${FontFamily.BarlowSemiCondensed}${FontStyleFile.Regular}`,
      file: '/fonts/Barlow-Semi-Condensed-Regular.ttf',
    },
    [FontStyle.Bold]: {
      key: `${FontFamily.BarlowSemiCondensed}${FontStyleFile.Bold}`,
      file: '/fonts/Barlow-Semi-Condensed-Bold.ttf',
    },
    [FontStyle.Italic]: {
      key: `${FontFamily.BarlowSemiCondensed}${FontStyleFile.Italic}`,
      file: '/fonts/Barlow-Semi-Condensed-Italic.ttf',
    },
    [FontStyle.BoldItalic]: {
      key: `${FontFamily.BarlowSemiCondensed}${FontStyleFile.BoldItalic}`,
      file: '/fonts/Barlow-Semi-Condensed-BoldItalic.ttf',
    },
  },
  [CustomFontName.BodoniModa]: {
    [FontStyle.Regular]: {
      key: `${FontFamily.BodoniModa}${FontStyleFile.Regular}`,
      file: '/fonts/Bodoni-Moda-Regular.ttf',
    },
    [FontStyle.Bold]: {
      key: `${FontFamily.BodoniModa}${FontStyleFile.Bold}`,
      file: '/fonts/Bodoni-Moda-Bold.ttf',
    },
    [FontStyle.Italic]: {
      key: `${FontFamily.BodoniModa}${FontStyleFile.Italic}`,
      file: '/fonts/Bodoni-Moda-Italic.ttf',
    },
    [FontStyle.BoldItalic]: {
      key: `${FontFamily.BodoniModa}${FontStyleFile.BoldItalic}`,
      file: '/fonts/Bodoni-Moda-BoldItalic.ttf',
    },
  },
  [CustomFontName.RampartOne]: {
    [FontStyle.Regular]: {
      key: `${FontFamily.RampartOne}${FontStyleFile.Regular}`,
      file: '/fonts/Rampart-One-Regular.ttf',
    },
  },
  [CustomFontName.GochiHand]: {
    [FontStyle.Regular]: {
      key: `${FontFamily.GochiHand}${FontStyleFile.Regular}`,
      file: '/fonts/Gochi-Hand-Regular.ttf',
    },
  },
  [CustomFontName.PlayfairDisplay]: {
    [FontStyle.Regular]: {
      key: `${FontFamily.PlayfairDisplay}${FontStyleFile.Regular}`,
      file: '/fonts/Playfair-Display-Regular.ttf',
    },
    [FontStyle.Bold]: {
      key: `${FontFamily.PlayfairDisplay}${FontStyleFile.Bold}`,
      file: '/fonts/Playfair-Display-Bold.ttf',
    },
    [FontStyle.Italic]: {
      key: `${FontFamily.PlayfairDisplay}${FontStyleFile.Italic}`,
      file: '/fonts/Playfair-Display-Italic.ttf',
    },
    [FontStyle.BoldItalic]: {
      key: `${FontFamily.PlayfairDisplay}${FontStyleFile.BoldItalic}`,
      file: '/fonts/Playfair-Display-BoldItalic.ttf',
    },
  },
  [CustomFontName.AmaticSC]: {
    [FontStyle.Regular]: {
      key: `${FontFamily.AmaticSC}${FontStyleFile.Regular}`,
      file: '/fonts/Amatic-SC-Regular.ttf',
    },
    [FontStyle.Bold]: {
      key: `${FontFamily.AmaticSC}${FontStyleFile.Bold}`,
      file: '/fonts/Amatic-SC-Bold.ttf',
    },
  },
  [CustomFontName.Inconsolata]: {
    [FontStyle.Regular]: {
      key: `${FontFamily.Inconsolata}${FontStyleFile.Regular}`,
      file: '/fonts/Inconsolata-Regular.ttf',
    },
    [FontStyle.Bold]: {
      key: `${FontFamily.Inconsolata}${FontStyleFile.Bold}`,
      file: '/fonts/Inconsolata-Bold.ttf',
    },
  },
  [CustomFontName.Caveat]: {
    [FontStyle.Regular]: {
      key: `${FontFamily.Caveat}${FontStyleFile.Regular}`,
      file: '/fonts/Caveat-Regular.ttf',
    },
    [FontStyle.Bold]: {
      key: `${FontFamily.Caveat}${FontStyleFile.Bold}`,
      file: '/fonts/Caveat-Bold.ttf',
    },
  },
  [CustomFontName.RussoOne]: {
    [FontStyle.Regular]: {
      key: `${FontFamily.RussoOne}${FontStyleFile.Regular}`,
      file: '/fonts/Russo-One-Regular.ttf',
    },
  },
};

export const CUSTOM_FONTS_FILES: FontFile = Object.fromEntries(
  Object.values(CUSTOM_FONTS).flatMap(font => Object.values(font).map(style => [style.key, style.file])),
);

export enum TextAlignment {
  Left = 'left',
  Center = 'center',
  Right = 'right',
  Justify = 'justify',
}

export const ALIGNMENT = [
  { key: TextAlignment.Left, icon: ICON_NAMES.FormatTextAlignLeft },
  { key: TextAlignment.Center, icon: ICON_NAMES.FormatTextAlignCenter },
  { key: TextAlignment.Right, icon: ICON_NAMES.FormatTextAlignRight },
];

export enum Formats {
  Eight = '8x10',
  Twelve = '12x15',
  Twenty = '20x25',
}

export enum FontSize {
  Eight = 8,
  Ten = 10,
  Twelve = 12,
  Fourteen = 14,
  Sixteen = 16,
  Eighteen = 18,
  Twenty = 20,
  TwentyTwo = 22,
  TwentyFour = 24,
  TwentySix = 26,
}
export const FONT_SIZES = Object.values(FontSize).filter(v => typeof v === 'number');

export const FORMAT_TO_FONT_SIZE: Record<Formats, number> = {
  [Formats.Eight]: FontSize.Ten,
  [Formats.Twelve]: FontSize.Sixteen,
  [Formats.Twenty]: FontSize.TwentySix,
};
